import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";

export interface JsonCodeEditorComponentStyles {
  root: AppSx;
  header: AppSx;
  label: (error: boolean) => AppSx;
  editorShell: (error: boolean, darkMode: boolean, borderRadius: number) => AppSx;
  helperText: AppSx;
  fullscreenButton: AppSx;
  fullscreenIcon: AppSx;
  fullscreenDialogContent: AppSx;
  fullscreenEditorBox: AppSx;
}

export function buildJsonCodeEditorComponentStyles(_theme: Theme): JsonCodeEditorComponentStyles {
  return {
    root: { width: "100%" },
    header: { mb: 0.75, minHeight: 24 },
    label: (error) => ({
      position: "relative",
      transform: "none",
      fontSize: "0.875rem",
      fontWeight: 600,
      color: error ? "error.main" : "text.secondary",
      m: 0,
    }),
    editorShell: (error, darkMode, borderRadius) => ({
      border: "1px solid",
      borderColor: error ? "error.main" : "divider",
      borderRadius: `${borderRadius}px`,
      overflow: "hidden",
      bgcolor: darkMode ? "#1e1e1e" : "#ffffff",
      transition: "border-color 0.2s",
      "&:focus-within": {
        borderColor: error ? "error.main" : "primary.main",
        boxShadow: (theme) =>
          error
            ? `0 0 0 1px ${theme.palette.error.main}`
            : `0 0 0 1px ${theme.palette.primary.main}`,
      },
      "& .monaco-editor": { borderRadius: 0 },
    }),
    helperText: { mx: 0, mt: 0.75 },
    fullscreenButton: {
      color: "text.secondary",
      "&:hover": { color: "primary.main", bgcolor: "action.hover" },
    },
    fullscreenIcon: { fontSize: 18 },
    fullscreenDialogContent: { display: "flex", flexDirection: "column", minHeight: 0, pt: 0 },
    fullscreenEditorBox: { flex: 1, minHeight: 0 },
  };
}

export interface PdfViewerComponentStyles {
  loadingRow: AppSx;
  canvasContainer: AppSx;
  signatureModal: AppSx;
  signaturePaper: AppSx;
  signatureTitle: AppSx;
  signatureCanvas: AppSx;
  signatureActions: AppSx;
}

export function buildPdfViewerComponentStyles(_theme: Theme): PdfViewerComponentStyles {
  return {
    loadingRow: { display: "flex", alignItems: "center", gap: 1.5, py: 2 },
    canvasContainer: {
      overflow: "auto",
      maxHeight: "60vh",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 1.25,
    },
    signatureModal: {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1000,
      p: 1.25,
    },
    signaturePaper: { p: 2, textAlign: "center" },
    signatureTitle: { mb: 1.5 },
    signatureCanvas: {
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      display: "block",
      mx: "auto",
    },
    signatureActions: { mt: 1.5 },
  };
}
